import { useEffect, useState } from "react";
import api from "../lib/api";
import { Avatar, Username, VipBadge } from "../components/UserBadge";
import { Link, useSearchParams } from "react-router-dom";
import { 
  Users, Crown, Shield, ShieldCheck, 
  Star, User, Award, Flame, Search, SlidersHorizontal, ArrowUpDown,
  Crosshair, MessageSquare, Trophy, Sparkles, ChevronRight, X
} from "lucide-react";

const iconMap = {
  users: Users,
  crown: Crown,
  shield: Shield,
  "shield-check": ShieldCheck,
  star: Star,
  user: User,
  award: Award,
  flame: Flame,
};

const GroupHeaderIcon = ({ icon, color, className }) => {
  if (icon && (icon.startsWith("http") || icon.includes("/"))) {
    return <img src={icon} alt="Icon" className={`${className} object-contain`} style={{ width: 18, height: 18 }} />;
  }
  const IconCmp = iconMap[icon] || Users;
  return <IconCmp className={className} style={{ color }} />;
};

function WeaponBadge({ name }) {
  if (!name) return null;
  const lower = name.toLowerCase();
  let color = "text-zinc-400 bg-zinc-900/60 border-zinc-800";
  let icon = "🔫";

  if (lower.includes("awp")) {
    color = "text-emerald-400 bg-emerald-950/40 border-emerald-500/30";
    icon = "🎯";
  } else if (lower.includes("ak") || lower.includes("m4")) {
    color = "text-amber-400 bg-amber-950/40 border-amber-500/30";
    icon = "⚡";
  } else if (lower.includes("deagle") || lower.includes("desert")) {
    color = "text-cyan-400 bg-cyan-950/40 border-cyan-500/30";
    icon = "💥";
  }

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-mono font-bold border ${color}`}>
      <span>{icon}</span>
      <span className="truncate max-w-[90px]">{name}</span>
    </span>
  );
}

export default function Members() {
  const [groupsData, setGroupsData] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("points");
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const groupFilter = searchParams.get("group");
  const [groupName, setGroupName] = useState("");
  const [legendGroups, setLegendGroups] = useState([]);

  const load = () => {
    setLoading(true);
    api.get(`/users/by-groups?q=${encodeURIComponent(q)}&sort=${sort}`)
      .then((r) => {
        setGroupsData(r.data || []);
      })
      .catch((err) => {
        console.error("Failed to load members:", err);
        setGroupsData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => { 
    load(); 
  }, [sort]);

  // Load group list for filter pills
  useEffect(() => {
    api.get("/idg/groups/legend")
      .then(r => {
        setLegendGroups(r.data || []);
        if (groupFilter) {
          const found = (r.data || []).find(g => g.id === groupFilter || g.slug === groupFilter);
          setGroupName(found?.name || "");
        }
      })
      .catch(() => {});
  }, [groupFilter]);

  const filteredGroups = groupFilter
    ? groupsData.filter((g) => g.id === groupFilter || g.slug === groupFilter)
    : groupsData;

  const totalMembersCount = groupsData.reduce((acc, g) => acc + (g.users?.length || 0), 0);

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12" data-testid="members-page">
      
      {/* ─── 1. HERO OPERATOR DIRECTORY HEADER ─── */}
      <div className="relative rounded-2xl overflow-hidden border border-[#161f30] bg-[#070a10] shadow-2xl p-6 sm:p-8">
        {/* Subtle Ambient Radial Glows */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[radial-gradient(ellipse_at_top_right,rgba(0,255,136,0.08),transparent_70%)] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[radial-gradient(ellipse_at_bottom_left,rgba(255,79,0,0.06),transparent_70%)] pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-[10px] font-mono font-black uppercase tracking-widest flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" />
                <span>INDUNGI // OPERATOR DIRECTORY</span>
              </span>
              <span className="text-[10px] font-mono text-zinc-400">
                · EST. 2010
              </span>
            </div>

            <h1 className="text-2xl sm:text-4xl font-chakra font-black uppercase text-white tracking-tight leading-none">
              DIRECTORUL OFICIAL <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--tf-primary)] via-amber-400 to-cyan-400">MEMBRI & OPERATORI</span>
            </h1>

            <p className="text-zinc-400 text-xs sm:text-sm font-sans leading-relaxed">
              Explorează comunitatea Indungi x PlayZek: conducere, staff tehnic, donatori VIP, lideri de clanuri și jucători activi de Counter-Strike.
            </p>
          </div>

          {/* Metric Stats Pills */}
          <div className="flex items-center gap-3 relative z-10 shrink-0">
            <div className="px-4 py-3 bg-[#05070d]/90 border border-[#1a2336] rounded-xl flex items-center gap-3 shadow-lg">
              <div className="w-10 h-10 rounded-lg bg-[var(--tf-primary)]/10 border border-[var(--tf-primary)]/30 flex items-center justify-center text-[var(--tf-primary)]">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[10px] font-mono text-zinc-400 uppercase tracking-wider">Total Înregistrați</div>
                <div className="text-xl font-chakra font-black text-white">{totalMembersCount || 96} Operatori</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── 2. GROUP FILTER PILLS ─── */}
      {legendGroups.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
          <button
            onClick={() => setSearchParams({})}
            className={`px-4 py-2 rounded-lg text-xs font-chakra font-black uppercase tracking-wider transition-all border shrink-0 ${
              !groupFilter 
                ? "bg-[var(--tf-primary)] text-black border-[var(--tf-primary)] shadow-md" 
                : "bg-[#080c14] border-[#161f30] text-zinc-400 hover:text-white hover:border-white/20"
            }`}
          >
            Toți Membrii
          </button>
          {legendGroups.map((lg) => {
            const isSelected = groupFilter === lg.id || groupFilter === lg.slug;
            return (
              <button
                key={lg.id}
                onClick={() => setSearchParams({ group: lg.id })}
                className={`px-4 py-2 rounded-lg text-xs font-chakra font-black uppercase tracking-wider transition-all border shrink-0 flex items-center gap-1.5 ${
                  isSelected 
                    ? "text-black border-transparent shadow-md" 
                    : "bg-[#080c14] border-[#161f30] text-zinc-400 hover:text-white hover:border-white/20"
                }`}
                style={isSelected ? { backgroundColor: lg.color || "var(--tf-primary)", borderColor: lg.color } : {}}
              >
                <span className="w-2 h-2 rounded-full shadow-sm" style={{ backgroundColor: lg.color || "#fff" }} />
                <span>{lg.name}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* ─── 3. SEARCH & SORT BAR ─── */}
      <div className="bg-[#080c14] border border-[#161f30] rounded-xl p-3.5 flex flex-wrap items-center justify-between gap-3 shadow-lg">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-zinc-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input 
            value={q} 
            onChange={(e) => setQ(e.target.value)} 
            onKeyDown={(e) => e.key === "Enter" && load()} 
            placeholder="Caută operator după nume sau rol..." 
            className="w-full bg-[#05070d] border border-[#141b2a] rounded-lg pl-10 pr-4 py-2.5 text-xs font-mono text-white placeholder:text-zinc-600 focus:outline-none focus:border-[var(--tf-primary)] transition-colors" 
            data-testid="members-search" 
          />
          {q && (
            <button 
              onClick={() => { setQ(""); load(); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 bg-[#05070d] border border-[#141b2a] px-3 py-1.5 rounded-lg text-xs font-mono text-zinc-400">
            <ArrowUpDown className="w-3.5 h-3.5 text-zinc-400" />
            <select 
              value={sort} 
              onChange={(e) => setSort(e.target.value)} 
              className="bg-transparent text-white focus:outline-none cursor-pointer uppercase font-bold text-[11px]" 
              data-testid="members-sort"
            >
              <option value="points" className="bg-[#080c14]">Top Puncte</option>
              <option value="messages" className="bg-[#080c14]">Cele mai multe Mesaje</option>
              <option value="recent" className="bg-[#080c14]">Recent Înregistrați</option>
              <option value="active" className="bg-[#080c14]">Recent Activi</option>
            </select>
          </div>

          <button
            onClick={load}
            className="px-5 py-2.5 rounded-lg bg-[var(--tf-primary)] hover:bg-[#ff6b2b] text-black font-chakra font-black text-xs uppercase tracking-wider transition-all shadow-md"
          >
            Caută
          </button>
        </div>
      </div>

      {/* ─── 4. ACTIVE GROUP FILTER NOTICE ─── */}
      {groupFilter && (
        <div className="flex items-center justify-between px-4 py-3 bg-[#080c14] border border-[var(--tf-primary)]/40 rounded-xl text-xs font-mono shadow-md">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[var(--tf-primary)] animate-pulse" />
            <span className="text-zinc-400">
              Grup filtrat: <strong className="text-white font-chakra text-sm uppercase">{groupName || groupFilter}</strong>
            </span>
          </div>
          <button
            onClick={() => setSearchParams({})}
            className="text-[11px] font-chakra font-bold text-zinc-400 hover:text-white border border-white/10 px-3 py-1 rounded-md hover:bg-white/5 transition-colors flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            <span>Resetează filtrul</span>
          </button>
        </div>
      )}

      {/* ─── 5. MEMBERS LIST BY GROUPS ─── */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 animate-pulse">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <div key={i} className="bg-[#080c14] border border-[#161f30] p-4 h-28 rounded-xl" />
          ))}
        </div>
      ) : filteredGroups.length === 0 ? (
        <div className="bg-[#080c14] border border-[#161f30] rounded-xl p-12 text-center text-zinc-400 font-mono text-xs">
          Niciun membru găsit conform criteriilor de căutare.
        </div>
      ) : (
        <div className="space-y-8">
          {filteredGroups.map((group) => (
            <div key={group.id} className="space-y-3.5">
              {/* Group Title Header */}
              <div className="flex items-center justify-between border-b border-[#141b2a] pb-2.5">
                <div className="flex items-center gap-2.5">
                  <div 
                    className="w-7 h-7 rounded-md flex items-center justify-center shadow-sm"
                    style={{ backgroundColor: `${group.color || 'var(--tf-primary)'}20`, border: `1px solid ${group.color || 'var(--tf-primary)'}40` }}
                  >
                    <GroupHeaderIcon icon={group.label_icon} color={group.color} className="w-4 h-4" />
                  </div>
                  <h2 
                    className="font-chakra text-base sm:text-lg font-black uppercase tracking-wide" 
                    style={{ color: group.color || "var(--tf-primary)" }}
                  >
                    {group.name}
                  </h2>
                </div>
                <span className="text-zinc-400 font-mono text-xs bg-[#05070d] px-2.5 py-0.5 rounded-full border border-white/5">
                  {group.users?.length || 0} membri
                </span>
              </div>

              {/* Members 4-Column Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {group.users.map((u) => {
                  const hasVip = (u.loadout?.banner_badge && u.loadout.banner_badge !== "none") || (u.vip_tier && u.vip_tier !== "none") || u.is_vip;

                  return (
                    <Link 
                      key={u.id} 
                      to={`/u/${u.username}`} 
                      className="group bg-[#080c14] hover:bg-[#0c121e] border border-[#161f30] hover:border-[var(--tf-primary)]/60 rounded-xl p-3.5 flex items-center gap-3 transition-all duration-200 shadow-md relative overflow-hidden hover:-translate-y-0.5"
                    >
                      {/* Avatar with Ring */}
                      <div className="relative shrink-0">
                        <Avatar user={u} size={48} hover={false} />
                        <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-[#080c14] shadow-[0_0_6px_#10b981]" title="Online" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        {/* Username & Badges */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Username 
                            user={u} 
                            showBadge={false} 
                            asText 
                            hover={false} 
                            className="text-xs font-black font-chakra truncate text-white group-hover:text-[var(--tf-primary)] transition-colors" 
                          />
                          
                          {u.role === "root" && (
                            <span className="px-1.5 py-0.2 text-[8px] font-black uppercase tracking-wider bg-amber-500 text-black font-chakra rounded">
                              ROOT
                            </span>
                          )}
                          {u.role === "admin" && u.role !== "root" && (
                            <span className="px-1.5 py-0.2 text-[8px] font-black uppercase tracking-wider bg-red-600 text-white font-chakra rounded">
                              ADMIN
                            </span>
                          )}
                          {hasVip && (
                            <span className="px-1.5 py-0.2 text-[8px] font-black uppercase tracking-wider bg-amber-400/20 text-amber-300 border border-amber-400/30 font-chakra rounded">
                              VIP
                            </span>
                          )}
                          {!hasVip && u.group_name && u.role !== "root" && u.role !== "admin" && (
                            <span 
                              className="px-1.5 py-0.2 text-[8px] font-black uppercase tracking-wider font-chakra rounded" 
                              style={{
                                backgroundColor: `${u.group_color || '#71717a'}25`,
                                color: u.group_color || '#fff',
                                border: `1px solid ${u.group_color || '#71717a'}40`
                              }}
                            >
                              {u.group_name}
                            </span>
                          )}
                        </div>

                        {/* Points & Posts */}
                        <div className="text-[10px] text-zinc-400 font-mono mt-1 flex items-center gap-1.5 flex-wrap">
                          <span className="text-amber-400 font-bold">★ {u.points?.toLocaleString() || 0}</span>
                          <span className="text-zinc-600">·</span>
                          <span>💬 {u.message_count || 0}</span>
                        </div>

                        {/* Favorite Weapon */}
                        {(u.loadout?.favorite_weapon || u.favorite_weapon) && (
                          <div className="mt-1">
                            <WeaponBadge name={u.loadout?.favorite_weapon || u.favorite_weapon} />
                          </div>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
